// Imports
import { useEffect, useRef } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom';


// How many pixels Chrome adds when correcting scroll position on refresh
const chromeScrollCorrectionBugThreshold = 60;


// How long to keep correcting after the content loads
const scrollCorrectionIdleDelay = 300;


// Function that handles checking for support
const sessionStorageAvailable = () => {
	try {
		const testKey = 'test-session-storage-support';
		sessionStorage.setItem(testKey, testKey);
		sessionStorage.removeItem(testKey);
		return true;
	} catch (e) {
		return false;
	}
};


// A hook that corrects Chrome's buggy scroll jump on reload
const useCorrectChromeScrollJump = (contentLoaded: boolean) => {
	// Use React Router functionality
	const location = useLocation();
	const navigationType = useNavigationType();
	
	
	// Use ref
	const userTookOverRef = useRef(false);
	
	
	// Stop correcting once the user takes over the scroll position
	useEffect(() => {
		// Record that the scroll position now belongs to the user
		const recordUserTakeOver = () => {
			userTookOverRef.current = true;
		};
		
		window.addEventListener('wheel', recordUserTakeOver);
		window.addEventListener('pointerdown', recordUserTakeOver);
		window.addEventListener('keydown', recordUserTakeOver);
		
		
		// Clean up
		return () => {
			window.removeEventListener('wheel', recordUserTakeOver);
			window.removeEventListener('pointerdown', recordUserTakeOver);
			window.removeEventListener('keydown', recordUserTakeOver);
		};
	}, []);
	
	
	// Identify this page's scroll position in session storage
	const savedScrollPositionKey = `scroll:${location.pathname}`;
	
	
	// Reset state
	useEffect(() => {
		userTookOverRef.current = false;
	}, [savedScrollPositionKey]);
	
	
	// Save scroll position when refreshing or navigating away
	useEffect(() => {
		// Skip when the scroll position can't be persisted
		if (!sessionStorageAvailable()) {
			return;
		}
		
		
		// Save the current scroll position
		const savePosition = () => {
			sessionStorage.setItem(savedScrollPositionKey, String(window.scrollY));
		};
		
		window.addEventListener('beforeunload', savePosition);
		
		
		// Clean up
		return () => {
			// Stop saving on unload
			window.removeEventListener('beforeunload', savePosition);
			
			
			// Get existing saved position
			const existingScrollPosition = sessionStorage.getItem(savedScrollPositionKey);
			
			
			// Save position when required
			//  -> Allows scroll correction to work when under React Strict Mode
			if (window.scrollY > 0 || existingScrollPosition === null) {
				savePosition();
			}
		};
	}, [savedScrollPositionKey]);
	
	
	// Undo Chrome's scroll overshoot while the content loads
	useEffect(() => {
		// Skip when the scroll position can't be persisted
		if (!sessionStorageAvailable()) {
			return;
		}
		
		
		// Skip push/replace navigations that don't cause the bug
		if (navigationType !== NavigationType.Pop) {
			return;
		}
		
		
		// Get saved position
		const targetScrollPosition = parseFloat(sessionStorage.getItem(savedScrollPositionKey) ?? '0');
		
		
		// Skip if the real position is already at/past the bug threshold
		if (targetScrollPosition >= chromeScrollCorrectionBugThreshold) {
			return;
		}
		
		
		// Pull the vertical scroll position to the correct position if the page overshot past it
		const correctIfNeeded = () => {
			if (userTookOverRef.current) {
				return;
			}
			
			const overshoot = window.scrollY - targetScrollPosition;
			
			if (Math.abs(overshoot - chromeScrollCorrectionBugThreshold) < 1) {
				window.scrollTo(0, targetScrollPosition);
			}
		};
		
		correctIfNeeded();
		
		
		// Watch in case the jump occurs during load and height changes again
		const observer = new ResizeObserver(correctIfNeeded);
		observer.observe(document.body);
		
		
		// Stop retrying scroll correction once the content has loaded and settled
		const idleTimeout = contentLoaded
			? setTimeout(() => {
					observer.disconnect();
				}, scrollCorrectionIdleDelay)
			: undefined;
		
		
		// Clean up
		return () => {
			observer.disconnect();
			clearTimeout(idleTimeout);
		};
	}, [contentLoaded, navigationType, savedScrollPositionKey]);
};

export default useCorrectChromeScrollJump;
